import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { Button } from "@/components/Button/Button";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/components/FullSizeLoader/FullSizeLoader";
import { CalendarIcon } from "@/components/Icons/CalendarIcon";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { DocumentPaper } from "@/components/Paper/DocumentPaper";
import { useProject } from "@/hooks/Network/useProject";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { BookableAssetsGridView } from "@/modules/bookings/components/BookableAssetsGridView";
import { getBookingsExportFilename } from "@/modules/bookings/helpers";
import { canManageAnyBookableAsset } from "@/modules/bookings/permissions";
import { bookableAssetMutations } from "@/queries/bookableAssets";
import { useBookableAssetsQueries } from "@/queries/bookableAssets/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

import { ExportBookingsModal } from "./components/ExportBookingsModal";

export function Layout(): React.ReactNode {
  const slug = useSlug();
  const { name: projectName } = useProject();
  const hasPermission = usePermission();
  const { t } = useTranslation();
  const [isExportAllBookingsModalOpened, exportAllBookingsModalOpenHandlers] = useBool(false);
  const assetsQuery: ApiQueryParams<"getBookableAssetsV1"> = {
    HideUnpublished: !hasPermission((x) => x.assets.canManageAll || x.assets.canManageOwn),
  };

  const bookableAssetsQueries = useBookableAssetsQueries();
  const {
    data: assetsData,
    isPending: isPendingAssets,
    error: assetsError,
    hasNextPage: hasMoreAssets,
    fetchNextPage: fetchMoreAssets,
    isFetchingNextPage: isFetchingMoreAssets,
  } = useInfiniteQuery(bookableAssetsQueries.listInfinite({ query: assetsQuery }));
  const exportAllBookingsMutation = bookableAssetMutations.useExportAllBookings();

  const allAssets = useMemo(() => assetsData?.pages.flatMap((x) => x.items ?? []) ?? [], [assetsData]);

  if (assetsError) {
    return <ErrorPage error={assetsError} />;
  }
  if (isPendingAssets) {
    return <FullSizeLoader withPadding />;
  }

  return (
    <>
      <DocumentPaper
        title={t("page.bookings.list-assets.title")}
        subTitle={t("page.bookings.list-assets.subtitle")}
        actions={
          <>
            <Button
              type="link"
              href={`${routes.calendar.list({
                slug,
              })}?tab=reservations`}
              styling="secondary"
              icon={<CalendarIcon />}
            >
              {t("page.bookings.list-assets.actions.see-reservations")}
            </Button>
            {hasPermission(canManageAnyBookableAsset) && (
              <>
                <Button
                  icon={<Download01Icon />}
                  styling="secondary"
                  onClick={exportAllBookingsModalOpenHandlers.setTrue}
                  isLoading={exportAllBookingsMutation.isPending}
                >
                  {t("page.bookings.list-assets.actions.export")}
                </Button>
                <Button
                  type="link"
                  href={routes.bookings.createAsset({
                    slug,
                  })}
                  styling="primary"
                >
                  {t("page.bookings.list-assets.actions.new")}
                </Button>
              </>
            )}
          </>
        }
      >
        <BookableAssetsGridView
          onLoadMore={fetchMoreAssets}
          isLoading={isFetchingMoreAssets}
          hasMore={hasMoreAssets}
          assets={allAssets}
        />
      </DocumentPaper>

      <ExportBookingsModal
        onExport={(includeHistory) =>
          exportAllBookingsMutation.mutate({
            fileName: getBookingsExportFilename(projectName),
            includeHistory,
          })
        }
        isOpened={isExportAllBookingsModalOpened}
        onOpenChange={exportAllBookingsModalOpenHandlers.set}
      />
    </>
  );
}
